import * as L from "leaflet";
// import "leaflet.markercluster";
import { Adress } from "../model/jmApiResponses";
import { WORKER_MARKER_OPTIONS } from "../config/defaultMapInput";
import { IconController } from "../controllers/iconController";
import {
  TRANSPORT_COLUMN_LABELS,
  TRANSPORT_INTERNAL_LABELS,
} from "../config/transportLabels";

/**
 * Beschriftungen fuer die Popups.
 *
 * Zwei Schluesselraeume treffen hier aufeinander: die Alternativen-Karten
 * rechnen mit eigenen Bezeichnern ("Auto", "Bike & Ride"), die Best-Wert-Karte
 * reicht den Spaltenpraefix der Datenbank durch ("Bike_and_Ride"). Beide
 * muessen im Popup so heissen wie in der Legende daneben.
 *
 * Die Schluessel der beiden Listen ueberschneiden sich nicht - ein Name gilt
 * entweder als Praefix oder als interner Bezeichner, nie als beides.
 */
const TRANSPORT_DISPLAY_NAMES: Record<string, string> = {
  ...TRANSPORT_COLUMN_LABELS,
  ...TRANSPORT_INTERNAL_LABELS,
};

/** Returns the display name for a transport key, or the key itself if no mapping exists */
export function getTransportDisplayName(transport: string): string {
  return TRANSPORT_DISPLAY_NAMES[transport] || transport;
}
// import { log } from "console";

export function generateMarkers(
  adressData: Adress[],
  iconSelector: (id: number | string) => L.Icon | L.DivIcon | null,
  metricData: Record<number, Record<string, number>> | undefined = undefined,
  metricType: string = "default",
  mapType: string = "default",
): L.LayerGroup {
  let coords: number[] | L.LatLng = [];
  let marker: L.Marker;
  let id: number;
  let icon: L.Icon;
  const markerGroup: L.Marker[] = [];
  const start = Date.now();
  for (const adress of adressData) {
    // @ts-ignore
    if (adress.geo) {
      coords = adress.geo.coordinates;
    } else {
      continue;
    }
    // @ts-ignore
    id = adress.id;

    // @ts-ignore
    icon = iconSelector(id);

    if (coords && id && icon) {
      // we can be sure, that if coords exist, it will have length = 2
      // @ts-ignore
      coords = L.latLng(coords[1], coords[0]);
      marker = L.marker(coords, {
        icon: icon,
        opacity: WORKER_MARKER_OPTIONS.opacity,
      });
      // Construct the address string for the popup
      let addressString = `${adress.street}, ${adress.postal_code} ${adress.district}`;

      const metricTypeDisplayMapping: Record<string, string> = {
        co2: "CO₂ pro km",
        cost: "Kosten pro km",
        time_in_mins: "Zeit in Min.",
        sport: "Schritte pro km",
        distance: "Entfernung in km",
      };

      // construct metric difference mapping
      const metricDifferenceMapping: Record<string, string> = {
        co2: "CO₂ Differenz",
        cost: "Kosten Differenz",
        time_in_mins: "Zeit Differenz",
        sport: "Schritte Differenz",
        distance: "Entfernung Differenz",
      };

      // Add the metric data to the address string if it exists
      if (metricData && metricData[id]) {
        let metricValue;
        let displayText;
        let unit = "";

        if (mapType === "BestTransport") {
          // Add transport type for BestTransport map
          const transportType = metricData[id].transportType;
          if (transportType) {
            addressString += `; Bestes Verkehrsmittel: ${getTransportDisplayName(
              String(transportType),
            )}`;
          }
        } else if (mapType === "BestAlternative") {
          // Handle BestAlternative case with custom data structure
          const alternativeResult = metricData[id] as any;
          if (alternativeResult) {
            const improvement = alternativeResult.metricImprovement || 0;
            const improvementText =
              improvement > 0
                ? `+${improvement.toFixed(1)} %`
                : `${improvement.toFixed(1)} %`;

            // Create comprehensive popup content
            addressString = `
              <div style="min-width: 200px;">
                <h4 style="margin: 0 0 10px 0; color: #1976d2;">
                  ${alternativeResult.meetsConstraints ? "✅ Alternative gefunden" : "⚠️ Keine Alternative"}
                </h4>
                
                <div style="margin-bottom: 8px;">
                  <strong>Empfohlen:</strong> ${getTransportDisplayName(alternativeResult.transportType)}
                </div>
                
                <div style="margin-bottom: 8px;">
                  <strong>Verbesserung:</strong>
                  <br>
                  <span style="color: ${improvement >= 0 ? "#4caf50" : "#f44336"};">
                    ${improvementText}
                  </span>
                </div>
                
                
                ${
                  alternativeResult.legCount !== undefined
                    ? `<div><strong>Umstiege:</strong> ${alternativeResult.legCount}</div>`
                    : ""
                }
                
                ${
                  !alternativeResult.meetsConstraints
                    ? '<div style="color: #ff9800; font-size: 12px; margin-top: 8px;">Keine Alternative erfüllt alle Kriterien</div>'
                    : ""
                }
              </div>
            `;
          }
        } else if (mapType === "MultipleAlternatives") {
          const altResult = metricData[id] as any;
          if (altResult) {
            const count = altResult.alternativeCount || 0;
            const allAlts = altResult.allAlternatives || [];

            // Header
            const header =
              count > 0
                ? `${count} Alternative${count !== 1 ? "n" : ""} gefunden`
                : "Keine Alternative";

            // Alternatives list
            let altListHtml = "";
            if (allAlts.length > 0) {
              const altItems = allAlts
                .sort((a: any, b: any) => b.improvement - a.improvement)
                .map((alt: any) => {
                  const impText =
                    alt.improvement > 0
                      ? `+${alt.improvement.toFixed(1)}%`
                      : `${alt.improvement.toFixed(1)}%`;
                  const impColor = alt.improvement >= 0 ? "#4caf50" : "#f44336";
                  return `<div style="padding: 3px 0; border-bottom: 1px solid #eee;">
                    <strong>${getTransportDisplayName(alt.transport)}</strong>
                    <span style="float: right; color: ${impColor};">${impText}</span>
                  </div>`;
                })
                .join("");

              altListHtml = `
                <div style="max-height: 150px; overflow-y: auto; margin-top: 8px;">
                  ${altItems}
                </div>`;
            }

            addressString = `
              <div style="min-width: 220px;">
                <h4 style="margin: 0 0 8px 0; color: #1976d2;">
                  ${count > 0 ? "✅" : "⚠️"} ${header}
                </h4>
                <div style="margin-bottom: 4px;">
                  <strong>Aktuell:</strong> ${getTransportDisplayName(altResult.currentTransport)}
                </div>
                ${
                  altResult.meetsConstraints
                    ? `<div style="margin-bottom: 4px;">
                        <strong>Beste Empfehlung:</strong> ${getTransportDisplayName(altResult.transportType)}
                      </div>`
                    : ""
                }
                ${altListHtml}
              </div>
            `;
          }
        } else if (mapType === "RelativeDifference") {
          // Handle percentage difference case where metricData is directly the percentage value
          if (typeof metricData[id] === "number") {
            metricValue = (metricData[id] as unknown as number)
              .toFixed(2)
              .replace(".", ",");
            displayText = metricDifferenceMapping[metricType] || metricType;
            unit = "%";
          }
        } else {
          // Handle regular metric data case
          if (
            metricData[id][metricType] !== undefined &&
            metricData[id][metricType] !== null &&
            typeof metricData[id][metricType] === "number"
          ) {
            metricValue = metricData[id][metricType]
              .toFixed(2)
              .replace(".", ",");
            displayText =
              mapType === "AbsoluteDifference"
                ? metricDifferenceMapping[metricType] || metricType
                : metricTypeDisplayMapping[metricType] || metricType;

            // Set unit for AbsoluteDifference
            if (mapType === "AbsoluteDifference") {
              switch (metricType) {
                case "co2":
                  unit = " g";
                  break;
                case "cost":
                  unit = " €";
                  break;
                case "time_in_mins":
                  unit = " min";
                  break;
                case "sport":
                  unit = " Schritte";
                  break;
                case "distance":
                  unit = " km";
                  break;
              }
            }
          }
        }

        // Only add to addressString if we have metric values (not for BestTransport)
        if (
          mapType !== "BestTransport" &&
          metricValue !== undefined &&
          displayText !== undefined
        ) {
          addressString += `; ${displayText}: ${metricValue}${unit}`;
        }
      }

      marker.bindPopup(addressString);

      markerGroup.push(marker);
    } else {
      console.warn(
        `Missing data for marker. coords: ${coords}, id: ${id}, icon: ${icon}`,
      );
    }
  }
  return L.layerGroup(markerGroup);
}
